"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * 2026-05-11 — page-level toast surface for "order placed" feedback.
 *
 * Replaces the prior pattern of leaving ClosePositionModal open with a
 * `submitNotice` after a successful submit (founder report: "stuck on
 * modal instead of opening a pending limit order, could we make it
 * better user friendly like we did in the market bet page where it's
 * still waiting to be picked up?").
 *
 * Polymarket / Kalshi / Coinbase / eToro pattern: the action surface
 * (modal / form / panel) dismisses immediately on a successful submit;
 * a transient toast at the page edge surfaces the outcome (filled vs
 * resting), with a clear "what's next" affordance (Cancel / Open
 * Orders link).
 *
 * Listens for `granbay.order.placed` events dispatched by
 * ClosePositionModal (and any future producer — TradePanel could
 * adopt this too to unify the success-toast surface across the app).
 * Auto-dismisses after 6s (matching TradePanel's `setTimeout(...,
 * 6000)` cadence). Stack of one — newer events replace older ones
 * rather than queueing.
 *
 * Mounted in /home/page.tsx today; can be hoisted to the root layout
 * once a second producer comes online.
 */

interface OrderPlacedDetail {
  side: "buy" | "sell";
  orderId: string | null;
  filledShares: number;
  pendingShares: number;
  requestedShares: number;
  priceCents: number;
  isResting: boolean;
  marketSlug: string;
  outcomeSortOrder: number;
}

type Toast = {
  detail: OrderPlacedDetail;
  expiresAt: number;
};

export function OrderPlacedToast() {
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => {
    function onPlaced(e: Event) {
      const ev = e as CustomEvent<OrderPlacedDetail>;
      if (!ev.detail) return;
      setToast({ detail: ev.detail, expiresAt: Date.now() + 6000 });
    }
    window.addEventListener("granbay.order.placed", onPlaced as EventListener);
    return () =>
      window.removeEventListener(
        "granbay.order.placed",
        onPlaced as EventListener,
      );
  }, []);

  useEffect(() => {
    if (!toast) return;
    const remaining = toast.expiresAt - Date.now();
    if (remaining <= 0) {
      setToast(null);
      return;
    }
    const timer = setTimeout(() => setToast(null), remaining);
    return () => clearTimeout(timer);
  }, [toast]);

  if (typeof document === "undefined" || !toast) return null;

  const d = toast.detail;
  const sideLabel = d.side === "buy" ? "BUY" : "SELL";
  // Per centi-share semantics, the displayed numbers are whole-share
  // fractional. Trim trailing zeros for integer counts.
  const fmtSh = (centi: number): string => {
    const whole = centi / 100;
    if (whole === 0) return "0";
    return Number.isInteger(whole) ? String(whole) : whole.toFixed(2);
  };

  // Three cases, matching TradePanel's success-toast taxonomy:
  // 1) full fill (no pending)         — tone success
  // 2) partial fill + resting tail    — tone success
  // 3) zero fill, whole order resting — tone success (still successful;
  //    nothing went wrong, the matcher's just waiting on a buyer)
  let title: string;
  let detailLine: string;
  const fillsLine =
    d.filledShares > 0
      ? `✓ ${fmtSh(d.filledShares)} ${sideLabel} filled at avg ${d.priceCents}¢`
      : null;
  const restsLine =
    d.pendingShares > 0
      ? `○ ${fmtSh(d.pendingShares)} ${sideLabel} resting at ${d.priceCents}¢`
      : null;

  if (d.pendingShares === 0) {
    title = `${sideLabel} order filled`;
    detailLine = fillsLine ?? "";
  } else if (d.filledShares > 0) {
    title = `${sideLabel} order placed`;
    detailLine = [fillsLine, restsLine].filter(Boolean).join(" · ");
  } else {
    title = `${sideLabel} limit placed`;
    detailLine = restsLine ?? "";
  }

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 z-[60] -translate-x-1/2 transform px-4"
      style={{ maxWidth: "min(420px, calc(100vw - 2rem))" }}
    >
      <div
        className="rounded-xl border bg-[var(--color-surface)] px-3.5 py-2.5 shadow-lg"
        style={{
          borderColor:
            "color-mix(in srgb, var(--color-yes) 50%, transparent)",
          background:
            "color-mix(in srgb, var(--color-yes) 8%, var(--color-surface))",
        }}
      >
        <p
          className="text-[11px] font-bold uppercase tracking-[0.4px]"
          style={{ color: "var(--color-yes)" }}
        >
          {title}
        </p>
        <p className="mt-0.5 font-mono text-[12px] tabular-nums text-[var(--color-text)]">
          {detailLine}
        </p>
        {d.pendingShares > 0 && (
          <p className="mt-1 text-[10px] text-[var(--color-text3)]">
            Cancel anytime from{" "}
            <a
              href="/home?tab=orders"
              className="underline hover:text-[var(--color-text)]"
            >
              Open Orders
            </a>
            .
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
